import { useEffect, useState } from "react";
import { downloadStatement, getAccountById, getAccounts, getAccountStatement } from "../api/requests";
import { Account, IAccount } from "../components/models/account/Account";
import { ITransaction, Transaction } from "../components/models/transaction/Transaction";
import { ACCOUNT_TYPES, STATEMENT_FORMAT } from "../resources/enums";
import useFile from "./fileHook";
import { ISelectDropdownItem } from "../components/form-control/SelectDropdown";
import { getPaginationDataFromResponse } from "../utilities/miscFunctions";
import { ITablePaginationData } from "../components/elements/GTable/GTable";

function useAccounts() {
  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [refreshing, setRefreshing] = useState<boolean>(true);
  const [account, setAccount] = useState<Account>();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountSummary, setAccountSummary] = useState<{ openingBalance: number, closingBalance: number, totalCredit: number, totalDebit: number, totalCommissions: number, totalWithdrawals: number }>({
    openingBalance: 0.00,
    closingBalance: 0.00,
    totalCredit: 0.00,
    totalDebit: 0.00,
    totalCommissions: 0.00,
    totalWithdrawals: 0.00
  })
  const [paginationData, setPaginationData] = useState<ITablePaginationData>({
    total: 0,
    per_page: 0,
    total_pages: 0,
    current_page: 1,
    to: 0,
    from: 0
  });
  const { downloadFileFromBlob } = useFile();

  const ACCOUNTS_LIST_FOR_FORM = accounts.map((account: Account): ISelectDropdownItem => (
    {
      id: account.id,
      name: account.name,
      value: account.id,
    }
  ))

  const fetchAccounts = ({ query, limit = 10, type, agentId, from, to, companyId, page, branchId }: {
    query?: string,
    limit?: number,
    type?: ACCOUNT_TYPES,
    agentId?: string,
    from?: string,
    to?: string,
    companyId?: string;
    page?: number,
    branchId?: string;
  }) => {
    getAccounts(query, companyId, limit, type, agentId, from, to, page, branchId)
      .then((response: any) => {
        const _accounts = response?.data?.map((IAccount: IAccount) => new Account(IAccount));
        setAccounts(_accounts);

        setPaginationData(getPaginationDataFromResponse(response));
        return _accounts;
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingAccounts(false);
        setRefreshing(false);
      })
  }

  const fetchAccountById = (accountId: string) => {
    getAccountById(accountId)
      .then((response: any) => {
        setAccount(new Account(response));
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingAccounts(false);
        setRefreshing(false);
      })
  }

  const fetchAccountStatement = (accountId: string, from?: string, to?: string) => {
    getAccountStatement(accountId, from, to)
      .then((response: any) => {
        const _transactions = response?.transactions?.map((ITransaction: ITransaction) => new Transaction(ITransaction));
        setTransactions(_transactions);

        setAccountSummary({
          openingBalance: response?.opening_balance?.toFixed(2),
          closingBalance: response?.closing_balance?.toFixed(2),
          totalCredit: response?.total_credit?.toFixed(2),
          totalDebit: response?.total_debit?.toFixed(2), 
          totalCommissions: response.total_commissions?.toFixed(2), 
          totalWithdrawals: response.total_withdrawals?.toFixed(2)
        })
        setAccount(new Account(response.account));
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingAccounts(false);
        setRefreshing(false);
      })
  }

  const exportStatement = (accountId: string, from?: string, to?: string, format = STATEMENT_FORMAT.EXCEL) => {
    downloadStatement(accountId, from, to, format)
      .then((response: any) => {
        const disposition = response.headers['content-disposition'];
        const filename = disposition
          ?.split('filename=')[1]
          ?.replace(/"/g, '') || "statement." + format;
        downloadFileFromBlob(response.data, filename ?? "statement." + format);
      }).catch((error: any) => {
        console.log("request error", error);
      }).finally(() => {
        setLoadingAccounts(false);
        setRefreshing(false);
      })
  }

  return {
    accounts,
    loadingAccounts,
    refreshing,
    fetchAccounts,
    fetchAccountById,
    account,
    setAccounts,
    fetchAccountStatement,
    transactions,
    accountSummary,
    setLoadingAccounts,
    exportStatement,
    ACCOUNTS_LIST_FOR_FORM,
    paginationData, 
    setAccount
  };

}

export default useAccounts;